/**
 * Blog Snap Slider
 * A lightweight slider for blog posts that uses native browser scroll snap
 */

if (!customElements.get('blog-snap-slider')) {
  class BlogSnapSlider extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.prevButton = null;
      this.nextButton = null;
      this.dots = [];
      this.slideWidth = 0;
      this.slideGap = 24; // matches the gap-6 class (6 * 4 = 24px)
      this.currentIndex = 0;
    }

    connectedCallback() {
      this.slider = this.querySelector('[id^="blogSlider"]');
      this.prevButton = this.querySelector('[id^="prevBlog"]');
      this.nextButton = this.querySelector('[id^="nextBlog"]');
      this.dots = this.querySelectorAll('.pagination-dot');

      if (!this.slider) return;

      // Calculate slide width (including gap)
      if (this.slider.children.length > 0) {
        // Use the first slide's width as reference
        const firstSlide = this.slider.children[0];
        this.slideWidth = firstSlide.offsetWidth + this.slideGap;
      }

      this.setupNavigation();
      this.setupDots();
      this.setupScrollListener();
    }

    setupNavigation() {
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.scrollPrev());
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.scrollNext());
      }
    }

    setupDots() {
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.goToSlide(index);
        });
      });
    }

    setupScrollListener() {
      let scrollTimeout;
      this.slider.addEventListener('scroll', () => {
        // Debounce the scroll event
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          this.updateCurrentIndex();
          this.updateDots();
        }, 50);
      });
    }

    updateCurrentIndex() {
      const slideWidth = this.slider.querySelector('.snap-start')?.offsetWidth || 0;
      if (slideWidth > 0) {
        this.currentIndex = Math.round(this.slider.scrollLeft / (slideWidth + this.slideGap));
      }
    }

    updateDots() {
      this.dots.forEach((dot, i) => {
        // Check if this is an article pagination dot
        const isArticleDot = dot.classList.contains('article-pagination-dot');
        
        if (i === this.currentIndex) {
          // Active state
          if (isArticleDot) {
            dot.classList.add('bg-darkGreen');
            dot.classList.remove('bg-[#1E40364D]');
          } else {
            // Default colors for other sections
            dot.classList.add('bg-white');
            dot.classList.remove('bg-white/40');
          }
        } else {
          // Inactive state
          if (isArticleDot) {
            dot.classList.remove('bg-darkGreen');
            dot.classList.add('bg-[#1E40364D]');
          } else {
            // Default colors for other sections
            dot.classList.remove('bg-white');
            dot.classList.add('bg-white/40');
          }
        }
      });
    }

    goToSlide(index) {
      if (!this.slider) return;
      
      const slideWidth = this.slider.querySelector('.snap-start')?.offsetWidth || 0;
      if (slideWidth > 0) {
        this.slider.scrollTo({
          left: index * (slideWidth + this.slideGap),
          behavior: 'smooth'
        });
        this.currentIndex = index;
        this.updateDots();
      }
    }

    scrollPrev() {
      if (!this.slider) return;
      const newIndex = Math.max(0, this.currentIndex - 1);
      this.goToSlide(newIndex);
    }

    scrollNext() {
      if (!this.slider) return;
      const maxIndex = this.dots.length - 1;
      const newIndex = Math.min(maxIndex, this.currentIndex + 1);
      this.goToSlide(newIndex);
    }
  }

  customElements.define('blog-snap-slider', BlogSnapSlider);
}